/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import axios from "axios";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from "@chakra-ui/react";
import DisplayAmountWithDecimals from "./DisplayAmountWithDecimals";

const TradeCompleteConfirmModal: React.FC<{
  dataToProcess: any;
  isOpen: boolean;
  onClose(): void;
  triggerReload(): void;
  setSwalProps: any;
}> = ({ isOpen, onClose, triggerReload, dataToProcess, setSwalProps }) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [transactionReference, setTransactionReference] = useState<string>("");
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const userRole = localStorage.getItem("userRole");

  const closeModal = async () => {
    setTransactionReference("");
    setIsConfirmed(false); // Reset checkbox when closing
    onClose();
  };

  const handleSubmit = async () => {
    if (!transactionReference.trim()) {
      setSwalProps({
        show: true,
        icon: "warning",
        title: "Validation Error",
        html: "Please provide a transaction reference.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
      return;
    }

    try {
      setIsProcessing(true);

      const data = {
        username: localStorage.getItem("username"),
        userRole: userRole,
        id: dataToProcess.id,
        shortid: dataToProcess.shortid,
        subaccountName: dataToProcess.subaccount.name,
        status: "completed",
        transaction_reference: transactionReference.trim()
      }

      await axios.post(process.env.REACT_APP_API_URL + `/api/${userRole.toLowerCase()}/trade/action/update-trade-status`, data)
        .then(function (response) {
          if (response && response.data?.status === 'ok') {
            closeModal();
            setSwalProps({
              show: true,
              icon: "success",
              title: "Success",
              html: "Trade has been marked as completed",
              showConfirmButton: true,
              didClose: () => {
                triggerReload();
                setSwalProps({});
                setIsProcessing(false);
              },
            });
          } else {
            setIsProcessing(false);
          }
        })
        .catch(function (error) {
          setIsProcessing(false);
          setSwalProps({
            show: true,
            icon: "error",
            title: "Oops!",
            html: error.response.data.message,
            showConfirmButton: true,
            didClose: () => {
              setSwalProps({});
              setIsProcessing(false);
            },
          });
        });
    } catch (error: any) {
      setIsProcessing(false);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsProcessing(false);
        },
      });
    }
  };

  const formatAddress = (address?: string): string => {
    if (!address || address.length < 24) return "N/A";
    return `${address.slice(0, 15)}...${address.slice(-9)}`;
  };

  const isFormValid = transactionReference.trim() !== "" && isConfirmed;

  return (
    <>
      {dataToProcess && (
        <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
          <ModalOverlay className="bg-[#000] !opacity-30" />
          <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
            <ModalBody className="p-6 md:p-7">
              <div className="mb-4 flex items-start gap-3">
                <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="currentColor"
                  >
                    <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Mark Completed — Confirmation
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Review the details below before confirming. This action is
                    permanent.
                  </p>
                </div>
              </div>

              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                You are about to mark this trade as{" "}
                <span className="font-medium">completed</span>. This cannot be
                undone.
              </div>

              <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                <dl className="flex flex-col gap-3 text-sm">
                  <div className="flex">
                    <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                      Code
                    </dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {dataToProcess.shortid}
                    </dd>
                  </div>

                  {userRole === "superadmin" && (
                    <div className="flex">
                      <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                        Subaccount
                      </dt>
                      <dd className="font-medium text-gray-900 dark:text-white">
                        {dataToProcess.subaccount.name}
                      </dd>
                    </div>
                  )}

                  <div className="flex">
                    <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                      Pair
                    </dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {`${dataToProcess.from_asset}/${dataToProcess.to_asset} (${dataToProcess.to_network})`}
                    </dd>
                  </div>

                  <div className="flex">
                    <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                      Amount
                    </dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {dataToProcess.from_network === "fiat" ? (
                        <DisplayAmountWithDecimals
                          amount={Number(dataToProcess.from_amount)}
                          currency={dataToProcess.from_asset}
                          decimal={dataToProcess?.from_asset_decimal}
                        />
                      ) : (
                        dataToProcess.from_amount
                      )}{" "}
                      {dataToProcess.from_asset}
                    </dd>
                  </div>

                  <div className="flex md:col-span-2">
                    <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                      To Address
                    </dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {`${dataToProcess.to_address.nickname} (${formatAddress(
                        dataToProcess.to_address.address
                      )})`}
                    </dd>
                  </div>

                  <div className="flex md:col-span-2">
                    <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                      Reference
                    </dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {dataToProcess.reference}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="mb-5">
                <label
                  htmlFor="transactionReference"
                  className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100"
                >
                  Transaction Ref. <span className="text-red-500">*</span>
                </label>
                <input
                  id="transactionReference"
                  type="text"
                  autoComplete="off"
                  disabled={isProcessing}
                  value={transactionReference}
                  onChange={(e) => setTransactionReference(e.target.value)}
                  placeholder="Enter transaction reference"
                  className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-primary-500/0 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Provide the blockchain TX hash or bank transfer reference.
                </p>
              </div>

              <div className="mb-6 rounded-xl border border-gray-200 p-4 text-sm dark:border-white/10">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    id="confirmComplete"
                    checked={isConfirmed}
                    onChange={(e) => setIsConfirmed(e.target.checked)}
                    disabled={isProcessing}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-60"
                  />
                  <span className="text-gray-700 dark:text-gray-200">
                    I confirm this trade has been completed successfully and the
                    transaction reference is accurate. I understand this action
                    cannot be undone.
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => closeModal()}
                  isDisabled={isProcessing}
                  className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                >
                  Not now
                </Button>

                <Button
                  isDisabled={!isFormValid || isProcessing}
                  onClick={() => handleSubmit()}
                  isLoading={isProcessing}
                  loadingText="Processing..."
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  Mark Completed
                </Button>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default TradeCompleteConfirmModal;
